// The Bio's team card (?live=1; teams and matches, Ben 2026-10-30 — docs/modules/teams-and-matches.md
// "1. Bio"). The same module as the player card: drop a team on it and it shows the team. Built
// from what the League Table already reads (`/league-table`, `/matches`), so no new server route:
// the league and its flag, the place in the table, the record, goals, form, the last result and
// the next game. The History screen lists the team's league finish each season.
//
// Club colours would make it look like the club, but our database has none yet: the card keeps
// the site's own look.

import { Suspense, type PointerEvent as ReactPointerEvent } from "react";
import { startTeamTokenDragOnMove } from "../canvas/menu-drag.js";
import { useLeagueTable, useLiveLeagueChoices } from "../hooks/useLeagueTable.js";
import { useLiveQuery } from "../live/query.js";
import { liveApi } from "../live/api.js";
import { loadSeasonMatches, toProtoFixtures } from "../live/results.js";
import { fixtureLine, gameDate, ordinal, resultLine, signed, tablePosition, teamGames, type TeamRef } from "../live/team.js";
import { humanizeSeasonCode, teamForm } from "./league-table-view.js";
import { CountryFlag } from "./CountryFlag.js";
import { FormDot } from "./FormDot.js";

const DASH = "—";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function BioTeam({ team }: { team: TeamRef }) {
  const league = useLiveLeagueChoices().find((c) => c.id === team.competitionId) ?? null;
  const table = useLeagueTable(team.competitionId, team.season ?? undefined);
  const season = table?.seasonCode ?? null;
  const matches =
    useLiveQuery(season ? `matches:season:${team.competitionId}:${season}` : null, () =>
      loadSeasonMatches(liveApi(), team.competitionId, season as string),
    ) ?? [];

  const standing = table?.table.find((r) => r.teamId === team.id) ?? null;
  const position = table ? tablePosition(table.table, team.id) : null;
  const { played, upcoming } = teamGames(matches, team.id, todayIso());
  const last = played.at(-1) ?? null;
  const next = upcoming[0] ?? null;
  // The League Table's own form squares and tooltips, for the same five games.
  const form = teamForm(toProtoFixtures(matches), team.name);
  const leagueName = league?.label ?? table?.competition ?? DASH;

  return (
    <>
      <div className="bio-head">
        {/* No big name: it's in the search box above, as for a player (Ben, 2026-10-18). */}
        <div className="bio-legal bio-team-league">
          <CountryFlag code={league?.countryCode ?? null} />
          {leagueName}
          {season ? ` · ${humanizeSeasonCode(season)}` : ""}
        </div>
        <div className="bio-position">{position !== null ? `${ordinal(position)} of ${table?.table.length}` : "Not in this table"}</div>
      </div>
      <div className="bio-meta">
        <div className="bio-meta-row">
          <span className="bio-meta-label">Points</span>
          <span className="bio-meta-value">{standing ? `${standing.points} from ${standing.played} games` : DASH}</span>
        </div>
        <div className="bio-meta-row">
          <span className="bio-meta-label">Won / Drawn / Lost</span>
          <span className="bio-meta-value">{standing ? `${standing.won} / ${standing.drawn} / ${standing.lost}` : DASH}</span>
        </div>
        <div className="bio-meta-row">
          <span className="bio-meta-label">Goals for / against</span>
          <span className="bio-meta-value">
            {standing ? `${standing.goalsFor} / ${standing.goalsAgainst} (${signed(standing.goalDifference)})` : DASH}
          </span>
        </div>
        <div className="bio-meta-row">
          <span className="bio-meta-label">Last result</span>
          <span className="bio-meta-value bio-last-appearance">
            {last ? (
              <>
                <span className="bio-line">{resultLine(last)}</span>
                <span className="bio-line">{gameDate(last)}</span>
              </>
            ) : (
              DASH
            )}
          </span>
        </div>
        <div className="bio-meta-row">
          <span className="bio-meta-label">Next game</span>
          <span className="bio-meta-value bio-last-appearance">
            {next ? (
              <>
                <span className="bio-line">{fixtureLine(next)}</span>
                <span className="bio-line">{gameDate(next)}</span>
              </>
            ) : (
              DASH
            )}
          </span>
        </div>
      </div>
      {form.length > 0 && (
        <div className="bio-team-form">
          <span className="bio-meta-label">Form</span>
          <span className="bio-team-form-dots">
            {form.map((g, k) => (
              <FormDot key={k} game={g} competitionId={team.competitionId} season={season} />
            ))}
          </span>
        </div>
      )}
    </>
  );
}

/** One season's finish, its own answer (each season's table is its own call). It drags out as the
 *  team in that season (as a player's Career season does): a League Table opens that season with
 *  the team highlighted, a Matches shows that season's games, a Bio that season's card. */
function HistoryRow({ team, seasonCode }: { team: TeamRef; seasonCode: string }) {
  const table = useLeagueTable(team.competitionId, seasonCode);
  const standing = table?.table.find((r) => r.teamId === team.id) ?? null;
  const position = table && standing ? tablePosition(table.table, team.id) : null;
  const onPress = (e: ReactPointerEvent<HTMLDivElement>) => {
    // Only a season it played in this league: anywhere else there's no row of its to show.
    if (!standing || window.innerWidth <= 768 || e.button !== 0) return;
    e.stopPropagation();
    startTeamTokenDragOnMove(e.nativeEvent, { ...team, season: seasonCode });
  };
  return (
    <div className={`career-row bio-team-history-row${standing ? " team-draggable" : ""}`} onPointerDown={onPress}>
      <span className="career-season">{humanizeSeasonCode(seasonCode)}</span>
      <span className="career-club">{position !== null ? `${ordinal(position)} of ${table?.table.length}` : "Not in this league"}</span>
      <span className="career-apps">{standing ? `${standing.points} pts · ${signed(standing.goalDifference)}` : ""}</span>
    </div>
  );
}

/** The History screen: the team's finish in this league, every season it has, newest first. */
export function BioTeamHistory({ team }: { team: TeamRef }) {
  const league = useLiveLeagueChoices().find((c) => c.id === team.competitionId) ?? null;
  const table = useLeagueTable(team.competitionId, team.season ?? undefined);
  const seasons = table?.seasonCodes ?? [];
  return (
    <div className="bio-career">
      <div className="bio-career-label">{league?.label ?? DASH}, season by season</div>
      <div className="career-list">
        {/* Each season loads on its own, so the newest show while older ones are still coming. */}
        {seasons.map((s) => (
          <Suspense
            key={s}
            fallback={
              <div className="career-row bio-team-history-row">
                <span className="career-season">{humanizeSeasonCode(s)}</span>
                <span className="career-club bio-team-loading">Loading…</span>
              </div>
            }
          >
            <HistoryRow team={team} seasonCode={s} />
          </Suspense>
        ))}
      </div>
    </div>
  );
}
